import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { concat, defer, of } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';
import type { ChangeDetectorRef } from '@angular/core';
import type { AbstractControl } from '@angular/forms';
import type { TAbstractControl, TFormGroup } from '@ppl/ui/form';
import type { Observable, Subscription } from 'rxjs';

export function getControlValue$<T extends any>(control: TAbstractControl<T>): Observable<T> {
  return concat(
    of(control.value),
    control.valueChanges
  );
}

export function getFormStatusObserver<T>(control: TAbstractControl<T>) {
  return concat(
    defer(() => {
      return of(control.status);
    }),
    control.statusChanges
  );
}

export function getFormValueObserver<T>(control: TAbstractControl<T>) {
  return concat(
    defer(() => {
      return of(control.value);
    }),
    control.valueChanges
  );
}

export function getFormValueObserverFor<T, K extends keyof T>(
  form: TFormGroup<T>,
  formControlName: K
): Observable<T[K]> {
  return getFormValueObserver(form.get(formControlName));
}

export function getFormValueObserversFor<T, K extends keyof T>(
  form: TFormGroup<T>,
  formControlNames: K[]
): Observable<T[K]>[] {
  return formControlNames.map(formControlName => {
    return getFormValueObserverFor(form, formControlName);
  });
}

/**
 * Re-calculates the value and validation status of the entire controls tree.
 */
export function updateTreeValidity(group: UntypedFormGroup | UntypedFormArray | TFormGroup<any>): void {
  Object.keys(group.controls).forEach((key: string) => {
    const abstractControl = group.controls[key] as AbstractControl | UntypedFormGroup;

    if (abstractControl instanceof UntypedFormGroup || abstractControl instanceof UntypedFormArray) {
      updateTreeValidity(abstractControl);
    } else {
      abstractControl.updateValueAndValidity({ emitEvent: false });
    }
  });
}

export function markFormGroupTouched(formGroup: UntypedFormGroup | TFormGroup<any>) {
  if (formGroup.controls) {
    const keys = Object.keys(formGroup.controls);
    for (let i = 0; i < keys.length; i++) {
      const control = formGroup.controls[keys[i]];

      if (control instanceof UntypedFormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof UntypedFormArray) {
        control.markAsTouched({ onlySelf: true });
        control.controls.forEach(markFormGroupTouched);
      } else if (control instanceof UntypedFormGroup) {
        markFormGroupTouched(control);
      }
    }
  }
}

export function listenToValidationChanges(
  control: AbstractControl,
  changeDetectorRef: ChangeDetectorRef
) {
  let statusChangesSubscription: Subscription;
  if (control) {
    statusChangesSubscription = control.statusChanges.pipe(
      distinctUntilChanged(),
      tap(() => {
        changeDetectorRef.markForCheck();
        changeDetectorRef.detectChanges();
      })
    ).subscribe();
  }

  let validationChangesSubscription: Subscription;
  if (control && 'validationChanges' in control) {
    validationChangesSubscription = (control.validationChanges as Observable<void>).pipe(
      tap(() => {
        changeDetectorRef.markForCheck();
        changeDetectorRef.detectChanges();
      })
    ).subscribe();
  }

  return {
    statusChangesSubscription,
    validationChangesSubscription,
  };
}
